import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

import TestForm from "./TestForm";
import PdfViewer from "./PdfViewer";
import Player from "./Player";

import { validateAttributes, errorMessage } from "../utils/validations";
import useDidMountEffect from "../utils/useDidMountEffect";
import { lessonValidations } from "./validations";

const LessonForm = (props) => {
  const { sections, setSections, sectionId, lessonId, setLessonId } = props;
  const section = sections.find((s) => s.id === sectionId);
  const lesson = section.lessons.find((l) => l.id === lessonId);
  const [title, setTitle] = useState(lesson ? lesson.title : "");
  const [lessonType, setLessonType] = useState(
    lesson ? lesson.lesson_type : "video",
  );
  const [initialLessonType, setInitialLessonType] = useState(
    lesson ? lesson.lesson_type : null,
  );
  const [description, setDescription] = useState(
    lesson ? lesson.description : "",
  );
  const [questions, setQuestions] = useState(lesson ? lesson.questions : []);
  const [passPercentage, setPassPercentage] = useState(
    lesson ? lesson.passing_percentage : 100,
  );
  const [randomQuestions, setRandomQuestions] = useState(
    lesson ? lesson.random_questions : false,
  );
  const [questionPool, setQuestionPool] = useState(
    lesson ? lesson.question_pool : questions.length,
  );

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (lesson?.question_pool) return;
    setQuestionPool(questions.length);
  }, [questions]);

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  useDidMountEffect(() => {
    setFile(null);
  }, [lessonType]);

  const uploadFile = async () => {
    if (!file) return;

    try {
      const url = `/courses/presigned_url?filename=${file.name}&content_type=${file.type}`;

      // Get presigned URL and key
      const { data } = await axios.get(url, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
      });

      // Upload the file directly to S3
      await axios.put(data.url, file, {
        headers: {
          "Content-Type": file.type,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setUploadProgress(percentCompleted);
        },
      });

      return data.key;
    } catch (error) {
      console.error("Error uploading file:", error);
      setFile(null);
      return null;
    }
  };

  const postLesson = async () => {
    const fileData = file
      ? {
          key: await uploadFile(),
          content_type: file.type,
          byte_size: file.size,
        }
      : null;

    const response = await fetch(`/sections/${sectionId}/lessons`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
      body: JSON.stringify({
        lesson: {
          title: title,
          description: description,
          lesson_type: lessonType,
          file: fileData,
          questions: questions,
          random_questions: randomQuestions,
          pass_percentage: passPercentage,
          question_pool: questionPool,
        },
      }),
    });
    if (response.ok) {
      const data = await response.json();
      setSections(
        sections.map((updatedSection) =>
          updatedSection.id == sectionId ? data.section : updatedSection,
        ),
      );
      setLessonId(data.section.lessons.slice(-1)[0].id);
    } else {
      // TODO handle error messages
      console.log("ERROR");
    }
  };

  const patchLesson = async () => {
    const fileData = file
      ? {
          key: await uploadFile(),
          content_type: file.type,
          byte_size: file.size,
        }
      : null;

    const response = await fetch(
      `/sections/${sectionId}/lessons/${lesson.id}`,
      {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          lesson: {
            title: title,
            description: description,
            lesson_type: lessonType,
            file: fileData,
            questions: questions,
            random_questions: randomQuestions,
            pass_percentage: passPercentage,
            question_pool: questionPool,
          },
        }),
      },
    );
    if (response.ok) {
      const data = await response.json();
      setSections(
        sections.map((updatedSection) =>
          updatedSection.id == sectionId ? data.section : updatedSection,
        ),
      );
      setLessonId(lesson.id);
    } else {
      console.log("ERROR");
    }
  };

  useEffect(() => {
    setTitle(lesson ? lesson.title : "");
    setDescription(lesson ? lesson.description : "");
    setLessonType(lesson ? lesson.lesson_type : "video");
    setFile(null);
    setUploadProgress(0);
    setRandomQuestions(lesson ? lesson.random_questions : false);
    setPassPercentage(lesson ? lesson.passing_percentage : 100);
    setQuestionPool(lesson ? lesson.question_pool : questions.length);
  }, [lesson]);

  const handleSubmit = async () => {
    let fields = { title };
    if (["video", "presentation"].includes(lessonType)) {
      fields = { ...fields, file };
    }
    if (lessonType === "test") {
      fields = { ...fields, questions };
    }

    const validation = validateAttributes(fields, lessonValidations);

    setErrors(validation);

    if (validation) return;

    setLoading(true);
    if (lesson) {
      await patchLesson();
    } else {
      await postLesson();
    }
    setLoading(false);
    setUploadProgress(0);
    setInitialLessonType(lessonType);
  };

  const lessonTypeClassName = (type) => {
    let className = "flex items-center gap-1 p-2 w-60 justify-center";
    if (lessonType === type) {
      className += " bg-black text-white";
    } else {
      className += " border border-primaryYellow text-primaryYellow";
    }

    return className;
  };

  const fileUploadComponent = () => {
    return (
      <div className="flex items-center justify-center w-full mt-10 flex-col border border-gray-200 p-4">
        <label
          htmlFor="dropzone-file"
          className="text-center border-2 border-primaryYellow text-primaryYellow w-full cursor-pointer"
        >
          <div className="flex items-center justify-center pt-6 pb-6">
            {file ? (
              <>{file.name}</>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m6.75 12-3-3m0 0-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                  />
                </svg>
                Wybierz z dysku
              </>
            )}
          </div>
          <input
            id="dropzone-file"
            type="file"
            className="hidden"
            title=" "
            onChange={(e) => handleFileChange(e)}
            accept={lessonType == "video" ? "video/mp4" : ".pdf"}
          />
        </label>

        {errorMessage(errors, "file")}
      </div>
    );
  };

  const className = (attribute) => {
    let className = "w-full mt-1 border px-3 py-2 text-sm";
    if (errors && errors[attribute]) {
      className += " border border-red-500 focus:ring-red-500";
    }
    return className;
  };

  return (
    <div className="w-full max-w-xl 2xl:max-w-2xl p-6 flex flex-col gap-2">
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <CircularProgressbar
            value={uploadProgress}
            className="h-40"
            styles={buildStyles({
              pathColor: "#A98842",
            })}
          />
        </div>
      )}
      <div className="flex items-center gap-2 mb-4">
        <button
          className={lessonTypeClassName("video")}
          onClick={() => setLessonType("video")}
        >
          Wideo
        </button>
        <button
          className={lessonTypeClassName("presentation")}
          onClick={() => setLessonType("presentation")}
        >
          Prezentacja
        </button>
        <button
          className={lessonTypeClassName("test")}
          onClick={() => setLessonType("test")}
        >
          Test
        </button>
      </div>

      {lesson && lessonType == "video" && lesson.file_url && (
        <Player
          fileUrl={lesson.file_url}
          initialLessonType={initialLessonType}
          sectionId={sectionId}
          lessonId={lessonId}
          videoDuration={lesson.video_length}
        />
      )}

      {lesson && lessonType == "presentation" && lesson.file_url && (
        <PdfViewer fileUrl={lesson.file_url} />
      )}

      <div>
        <label className="block text-sm font-medium text-black mb-1">
          Tytuł
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={className("title")}
          placeholder="Tytuł"
        />
        {errorMessage(errors, "title")}
      </div>

      <div>
        <label className="block text-sm font-medium text-black mb-1">
          Opis (opcjonalnie)
        </label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full mt-1 border px-3 py-2 text-sm"
          placeholder="Opis..."
        />
      </div>

      {["video", "presentation"].includes(lessonType) && fileUploadComponent()}
      {lessonType === "test" && (
        <>
          <TestForm
            questions={questions}
            setQuestions={setQuestions}
            passPercentage={passPercentage}
            setPassPercentage={setPassPercentage}
            randomQuestions={randomQuestions}
            setRandomQuestions={setRandomQuestions}
            questionPool={questionPool}
            setQuestionPool={setQuestionPool}
          />
          {errorMessage(errors, "questions")}
        </>
      )}

      <button
        type="submit"
        className="gap-2 bg-black text-white px-4 py-2 w-24 mt-10 ml-auto"
        onClick={handleSubmit}
      >
        Zapisz
      </button>
    </div>
  );
};

LessonForm.propTypes = {
  sectionId: PropTypes.string,
  lessonId: PropTypes.string,
  sections: PropTypes.array,
  setSections: PropTypes.func,
  setLessonId: PropTypes.func,
};

export default LessonForm;
